import { useRef, useEffect } from 'react';
import * as Icons from 'react-icons/fa';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { servicesData } from '../../data/servicesData';
import MarqueeTicker from '../Common/MarqueeTicker';

gsap.registerPlugin(ScrollTrigger);

const ServicesSection = () => {
  const headingRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from('.svc-line', {
        y: 40, opacity: 0, duration: 0.9, stagger: 0.08,
        ease: 'power3.out',
        scrollTrigger: { trigger: headingRef.current, start: 'top 85%', once: true },
      });
    });
    return () => ctx.revert();
  }, []);

  return (
    <section
      id="services"
      className="w-full bg-[#FAFBF9] border-t border-[#F1F3F1] relative overflow-hidden"
    >
      {/* ── Heading ─────────────────────────────── */}
      <div
        ref={headingRef}
        className="px-6 sm:px-10 lg:px-16 pt-28 sm:pt-40 pb-10 max-w-7xl mx-auto relative z-10"
      >
        <div className="overflow-hidden mb-3">
          <span className="svc-line inline-block text-[11px] uppercase tracking-[0.28em] text-[#5A635A] font-semibold">
            ✦&nbsp;&nbsp;Capabilities&nbsp;&nbsp;✦
          </span>
        </div>

        <div className="overflow-hidden mb-1">
          <h2 className="svc-line text-[clamp(2.8rem,8vw,6.5rem)] font-normal leading-[1.0] tracking-tight text-black/20">
            Full-stack digital.
          </h2>
        </div>
        <div className="overflow-hidden mb-10">
          <h2 className="svc-line text-[clamp(2.8rem,8vw,6.5rem)] font-black leading-[1.0] tracking-tight text-black">
            <span className="bg-gradient-to-r from-[#FF7332] via-[#FF1E90] to-[#7000FF] bg-clip-text text-transparent inline pb-2">
              Built to perform.
            </span>
          </h2>
        </div>

        {/* Description + stat pills */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 lg:gap-20">
          <div className="overflow-hidden">
            <p className="svc-line text-base sm:text-lg text-[#5A635A] font-normal max-w-xl leading-relaxed">
              Every channel, every stack — one integrated team. We ship measurable results, not slide decks.
            </p>
          </div>
          <div className="svc-line flex gap-2.5 flex-wrap flex-shrink-0">
            {[
              { n: '500+',  l: 'Projects Delivered' },
              { n: '98%',   l: 'Client Retention'   },
              { n: '24 hr', l: 'Response SLA'        },
            ].map((s, i) => (
              <div
                key={i}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-[#EAECE9] rounded-full shadow-sm"
              >
                <span className="text-sm font-bold text-black tabular-nums">{s.n}</span>
                <span className="text-[11px] text-[#5A635A] font-semibold">{s.l}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Marquee ─────────────────────────────── */}
      <div className="border-t border-b border-[#EAECE9] py-4 bg-white/60 relative z-10 mt-8">
        <MarqueeTicker speed={38} />
      </div>

      {/* ── Card Grid ───────────────────────────── */}
      <div className="px-6 sm:px-10 lg:px-16 pb-10 max-w-7xl mx-auto relative z-10 mt-14 sm:mt-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {servicesData.map((service, i) => {
            const IconComponent = Icons[service.icon];
            const isHero = i === 0;

            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-30px' }}
                transition={{ duration: 0.45, delay: Math.min(i * 0.05, 0.22), ease: 'easeOut' }}
                className={isHero ? 'md:col-span-2 lg:col-span-2' : ''}
              >
                <Link to={`/services/${service.slug}`} className="block h-full group">

                  {/* ── Wide hero card (index 0) ── */}
                  {isHero ? (
                    <div className="relative overflow-hidden rounded-[24px] bg-white border border-[#EAECE9]
                      p-9 sm:p-11 flex flex-col sm:flex-row sm:gap-10 min-h-[280px]
                      hover:border-[#1C2E1E] hover:shadow-md
                      transition-[border-color,box-shadow] duration-200 shadow-sm">

                      {/* Subtle corner glow */}
                      <div
                        className="absolute top-0 left-0 w-72 h-72 pointer-events-none rounded-[24px]"
                        style={{ background: 'radial-gradient(ellipse at 0% 0%, rgba(255,115,50,0.06) 0%, transparent 60%)' }}
                      />

                      {/* Large watermark */}
                      <div className="absolute bottom-6 right-8 text-[110px] sm:text-[130px] font-black text-black/[0.03] leading-none select-none pointer-events-none">
                        01
                      </div>

                      {/* Left — icon area */}
                      <div className="relative z-10 flex flex-col justify-between flex-shrink-0 sm:w-52">
                        <div>
                          <span className="text-[10px] uppercase tracking-[0.22em] text-[#5A635A] font-semibold px-3 py-1.5 border border-[#EAECE9] rounded-full inline-block mb-7">
                            Core Service
                          </span>
                          <div className="w-16 h-16 bg-[#F4F5F4] border border-[#EAECE9] rounded-2xl flex items-center justify-center
                            group-hover:bg-[#1C2E1E] group-hover:border-[#1C2E1E] transition-colors duration-200">
                            {IconComponent && (
                              <IconComponent size={24} className="text-[#1C2E1E] group-hover:text-white transition-colors duration-200" />
                            )}
                          </div>
                        </div>
                        <span className="text-[11px] font-semibold text-[#5A635A]/35 tabular-nums mt-7 sm:mt-0">01</span>
                      </div>

                      {/* Vertical divider (desktop) */}
                      <div className="hidden sm:block w-px bg-[#F1F3F1] self-stretch flex-shrink-0" />

                      {/* Right — text */}
                      <div className="relative z-10 flex flex-col justify-between flex-1 mt-8 sm:mt-0">
                        <div>
                          <h3 className="text-2xl sm:text-[28px] font-bold text-black tracking-tight leading-tight mb-4">
                            {service.title}
                          </h3>
                          <p className="text-sm text-[#5A635A] leading-relaxed font-normal max-w-prose">
                            {service.shortDesc}
                          </p>
                        </div>

                        <div className="flex items-center gap-2 pt-7 mt-7 border-t border-[#F1F3F1] text-[11px] font-semibold uppercase tracking-wider text-[#1C2E1E]/35 group-hover:text-[#1C2E1E] transition-colors duration-200">
                          <span>Explore Service</span>
                          <Icons.FaArrowRight size={9} className="group-hover:translate-x-1 transition-transform duration-200" />
                        </div>
                      </div>
                    </div>

                  ) : (
                    /* ── Standard vertical cards ── */
                    <div className="relative overflow-hidden rounded-[24px] bg-white border border-[#EAECE9]
                      p-8 sm:p-9 flex flex-col min-h-[260px]
                      hover:border-[#1C2E1E] hover:shadow-md
                      transition-[border-color,box-shadow] duration-200 shadow-sm">

                      {/* Faded watermark */}
                      <div className="absolute bottom-4 right-6 text-[5.5rem] font-black text-black/[0.03] leading-none select-none pointer-events-none">
                        {String(i + 1).padStart(2, '0')}
                      </div>

                      {/* Top — icon + number */}
                      <div className="flex items-start justify-between mb-auto">
                        <div className="w-12 h-12 bg-[#F4F5F4] border border-[#EAECE9] rounded-xl flex items-center justify-center
                          group-hover:bg-[#1C2E1E] group-hover:border-[#1C2E1E] transition-colors duration-200">
                          {IconComponent && (
                            <IconComponent size={17} className="text-[#1C2E1E] group-hover:text-white transition-colors duration-200" />
                          )}
                        </div>
                        <span className="text-[11px] font-semibold text-[#5A635A]/35 tabular-nums">
                          {String(i + 1).padStart(2, '0')}
                        </span>
                      </div>

                      {/* Content */}
                      <div className="mt-9">
                        <h3 className="text-[17px] font-bold text-black mb-2.5 leading-snug tracking-tight">
                          {service.title}
                        </h3>
                        <p className="text-[13px] text-[#5A635A] leading-relaxed font-normal">
                          {service.shortDesc}
                        </p>
                      </div>

                      {/* Bottom link */}
                      <div className="flex items-center gap-2 pt-5 mt-6 border-t border-[#F1F3F1] text-[11px] font-semibold uppercase tracking-wider text-[#1C2E1E]/35 group-hover:text-[#1C2E1E] transition-colors duration-200">
                        <span>Explore Service</span>
                        <Icons.FaArrowRight size={9} className="group-hover:translate-x-1 transition-transform duration-200" />
                      </div>
                    </div>
                  )}
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* ── Bottom CTA ──────────────────────────── */}
      <div className="px-6 sm:px-10 lg:px-16 pb-28 sm:pb-40 max-w-7xl mx-auto relative z-10 mt-14 sm:mt-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="relative overflow-hidden rounded-[32px] bg-[#1C2E1E] px-10 sm:px-16 py-14 sm:py-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-10"
        >
          {/* Radial accent */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: 'radial-gradient(ellipse at 5% 50%, rgba(255,115,50,0.14) 0%, transparent 50%)' }}
          />

          <div className="relative z-10">
            <span className="text-[11px] uppercase tracking-[0.28em] text-white/50 font-semibold block mb-5">
              ✦&nbsp;&nbsp;Custom Scope&nbsp;&nbsp;✦
            </span>
            <h3 className="text-3xl sm:text-4xl font-bold text-white tracking-tight leading-[1.05]">
              Need a{' '}
              <span className="bg-gradient-to-r from-[#FF7332] via-[#FF1E90] to-[#7000FF] bg-clip-text text-transparent inline pb-1">
                tailored<br />digital stack?
              </span>
            </h3>
          </div>

          <div className="relative z-10 flex-shrink-0 flex flex-col sm:items-end gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2.5 px-8 py-4 bg-white text-[#1C2E1E] rounded-full text-sm font-semibold tracking-wide hover:bg-[#F4F5F4] transition-colors duration-200 group"
            >
              Get Custom Quote
              <Icons.FaArrowRight size={11} className="group-hover:translate-x-1 transition-transform duration-200" />
            </Link>
            <Link
              to="/services"
              className="text-[11px] font-semibold uppercase tracking-wider text-white/50 hover:text-white/75 transition-colors duration-200 inline-flex items-center gap-2 group"
            >
              Browse All Services
              <Icons.FaArrowRight size={8} className="group-hover:translate-x-1 transition-transform duration-200" />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
